import { supabase } from "@/lib/supabase";
import type { User } from "@supabase/supabase-js";
import { normalizeUserRole } from "@/data/RoleContext";

let cachedOrgId: string | null = null;
let cachedUserRole: string | null = null;
let cachedAuthUser: User | null | undefined;
let pendingAuthUserPromise: Promise<User | null> | null = null;
let pendingOrgIdPromise: Promise<string> | null = null;
let pendingUserRolePromise: Promise<string | null> | null = null;

const PENDING_SETUP_ORG_ID = "pending-setup";

type OrganizationResolutionOptions = {
  allowPendingSetup?: boolean;
};

function isUuidLike(value: unknown): value is string {
  return typeof value === "string"
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

async function resolveCompanySettingsOrganizationId(): Promise<string | null> {
  const { data: settings, error } = await supabase
    .from("app_settings")
    .select("organization_id")
    .eq("setting_type", "company")
    .maybeSingle();

  if (error) {
    console.error("Error fetching company settings organization:", error);
    return null;
  }

  return isUuidLike(settings?.organization_id) ? settings.organization_id : null;
}

async function resolveContractorCrewOrganizationId(authUserId: string): Promise<string | null> {
  const { data: profiles, error: profilesError } = await supabase
    .from("profiles")
    .select("id")
    .or(`auth_user_id.eq.${authUserId},id.eq.${authUserId}`);

  if (profilesError) {
    console.error("Error fetching candidate profile ids for organization resolution:", profilesError);
    return null;
  }

  const candidateUserIds = Array.from(new Set([
    authUserId,
    ...((profiles ?? []) as Array<{ id?: string | null }>)
      .map((profile) => profile.id)
      .filter(isUuidLike),
  ]));

  if (candidateUserIds.length === 0) return null;

  const { data: crew, error: crewError } = await supabase
    .from("contractor_crews")
    .select("contractor_id")
    .in("user_id", candidateUserIds)
    .eq("status", "active")
    .limit(1)
    .maybeSingle();

  if (crewError) {
    console.error("Error fetching active contractor crew for organization resolution:", crewError);
    return null;
  }

  if (!isUuidLike(crew?.contractor_id)) return null;

  const { data: contractor, error: contractorError } = await supabase
    .from("contractors")
    .select("organization_id")
    .eq("id", crew.contractor_id)
    .maybeSingle();

  if (contractorError) {
    console.error("Error fetching contractor organization for organization resolution:", contractorError);
    return null;
  }

  return isUuidLike(contractor?.organization_id) ? contractor.organization_id : null;
}

async function resolveFallbackOrganizationId(authUserId: string): Promise<string | null> {
  const crewOrgId = await resolveContractorCrewOrganizationId(authUserId);
  if (crewOrgId) return crewOrgId;

  const settingsOrgId = await resolveCompanySettingsOrganizationId();
  if (settingsOrgId) return settingsOrgId;

  const { data: organization, error } = await supabase
    .from("organizations")
    .select("id")
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("Error fetching fallback organization:", error);
    return null;
  }

  return isUuidLike(organization?.id) ? organization.id : null;
}

export function clearOrganizationCache(): void {
  cachedOrgId = null;
  cachedUserRole = null;
  cachedAuthUser = undefined;
  pendingAuthUserPromise = null;
  pendingOrgIdPromise = null;
  pendingUserRolePromise = null;
}

async function getCachedAuthUser(): Promise<User | null> {
  if (cachedAuthUser !== undefined) return cachedAuthUser;
  if (pendingAuthUserPromise) return pendingAuthUserPromise;
  pendingAuthUserPromise = (async () => {
    const { data: { user } } = await supabase.auth.getUser();
    cachedAuthUser = user ?? null;
    return cachedAuthUser;
  })();
  try {
    return await pendingAuthUserPromise;
  } finally {
    pendingAuthUserPromise = null;
  }
}

async function resolveCurrentUserRole(): Promise<string | null> {
  const user = await getCachedAuthUser();

  if (!user) throw new Error("User not authenticated");

  const { data: profileByAuthUserId, error: profileByAuthUserIdError } = await supabase
    .from("profiles")
    .select("id, role")
    .eq("auth_user_id", user.id)
    .maybeSingle();

  if (profileByAuthUserIdError) {
    console.error("Error fetching profile for role resolution by auth_user_id:", profileByAuthUserIdError);
    return null;
  }

  let profile = profileByAuthUserId;
  if (!profile) {
    const { data: profileById, error: profileByIdError } = await supabase
      .from("profiles")
      .select("id, role")
      .eq("id", user.id)
      .maybeSingle();

    if (profileByIdError) {
      console.error("Error fetching profile for role resolution by id:", profileByIdError);
      return null;
    }

    profile = profileById;
  }

  if (!profile?.id) return null;

  const { data: userRole, error: userRoleError } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", profile.id)
    .maybeSingle();

  if (userRoleError) {
    console.error("Error fetching current user role from user_roles:", userRoleError);
    return null;
  }

  // user_roles is source of truth. Keep profile.role as transitional fallback only.
  cachedUserRole = normalizeUserRole((userRole as any)?.role ?? (profile as any)?.role ?? null);
  return cachedUserRole;
}

export async function getCurrentUserRole(): Promise<string | null> {
  if (cachedUserRole) return cachedUserRole;
  if (pendingUserRolePromise) return pendingUserRolePromise;
  pendingUserRolePromise = resolveCurrentUserRole();
  try {
    return await pendingUserRolePromise;
  } finally {
    pendingUserRolePromise = null;
  }
}

async function resolveCurrentOrganizationId(options: OrganizationResolutionOptions): Promise<string> {
  if (cachedOrgId && (options.allowPendingSetup || cachedOrgId !== PENDING_SETUP_ORG_ID)) {
    return cachedOrgId;
  }

  if (cachedOrgId === PENDING_SETUP_ORG_ID && !options.allowPendingSetup) {
    cachedOrgId = null;
  }

  const user = await getCachedAuthUser();

  if (!user) throw new Error("User not authenticated");

  // Use maybeSingle to avoid error when no profile exists. Some deployments
  // identify the profile by auth_user_id, others by id = auth user id.
  const { data: profileByAuthUserId, error } = await supabase
    .from("profiles")
    .select("organization_id")
    .eq("auth_user_id", user.id)
    .maybeSingle();

  if (error) {
    console.error("Error fetching profile organization:", error);
    throw error;
  }

  let profile = profileByAuthUserId;

  if (!profile) {
    const { data: profileById, error: profileByIdError } = await supabase
      .from("profiles")
      .select("organization_id")
      .eq("id", user.id)
      .maybeSingle();

    if (profileByIdError) {
      console.error("Error fetching profile organization by profile id:", profileByIdError);
      throw profileByIdError;
    }

    profile = profileById;
  }

  if (isUuidLike(profile?.organization_id)) {
    cachedOrgId = profile.organization_id;
    return cachedOrgId;
  }

  const fallbackOrgId = await resolveFallbackOrganizationId(user.id);
  if (fallbackOrgId) {
    cachedOrgId = fallbackOrgId;
    return cachedOrgId;
  }

  if (options.allowPendingSetup) {
    const role = await getCurrentUserRole();
    if (role === "super_admin" || role === "owner") {
      return cachedOrgId = PENDING_SETUP_ORG_ID;
    }
  }

  throw new Error("Missing organization_id");
}

export async function getCurrentOrganizationId(options: OrganizationResolutionOptions = {}): Promise<string> {
  if (cachedOrgId && (options.allowPendingSetup || cachedOrgId !== PENDING_SETUP_ORG_ID)) {
    return cachedOrgId;
  }
  if (!options.allowPendingSetup && pendingOrgIdPromise) return pendingOrgIdPromise;

  const promise = resolveCurrentOrganizationId(options);
  if (!options.allowPendingSetup) {
    pendingOrgIdPromise = promise;
  }

  try {
    return await promise;
  } finally {
    if (pendingOrgIdPromise === promise) {
      pendingOrgIdPromise = null;
    }
  }
}
